import type { ComponentProps, ReactNode } from 'react';
import * as RadixMenu from '@radix-ui/react-dropdown-menu';
import { Check } from 'lucide-react';
import { cn } from './cn';

/** Menu root and trigger (Radix): `<DropdownMenu><DropdownMenuTrigger asChild><Button/></…><DropdownMenuContent>…` */
export const DropdownMenu = RadixMenu.Root;
export const DropdownMenuTrigger = RadixMenu.Trigger;

/** The floating menu panel. */
export function DropdownMenuContent({ className, align = 'end', sideOffset = 6, ...props }: ComponentProps<typeof RadixMenu.Content>) {
  return (
    <RadixMenu.Portal>
      <RadixMenu.Content
        align={align}
        sideOffset={sideOffset}
        className={cn('z-50 min-w-48 rounded-lg border border-line bg-surface p-1 font-sans text-sm text-ink shadow-pop', className)}
        {...props}
      />
    </RadixMenu.Portal>
  );
}

/** One command. `danger` colours destructive items; `icon` is a leading lucide icon. */
export function DropdownMenuItem({ className, icon, danger, children, ...props }: ComponentProps<typeof RadixMenu.Item> & { icon?: ReactNode; danger?: boolean }) {
  return (
    <RadixMenu.Item
      className={cn(
        'flex h-8 cursor-pointer select-none items-center gap-2 rounded-md px-2 text-sm outline-none pointer-coarse:h-10 [&_svg]:size-4 [&_svg]:text-ink-faint',
        'data-[highlighted]:bg-muted data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        danger && 'text-danger-ink [&_svg]:text-danger-ink',
        className,
      )}
      {...props}
    >
      {icon}
      {children}
    </RadixMenu.Item>
  );
}

/** A set of mutually exclusive items (e.g. the language); `value` is the checked one. */
export const DropdownMenuRadioGroup = RadixMenu.RadioGroup;

/** One choice of a DropdownMenuRadioGroup, with a check when selected. */
export function DropdownMenuRadioItem({ className, children, ...props }: ComponentProps<typeof RadixMenu.RadioItem>) {
  return (
    <RadixMenu.RadioItem
      className={cn(
        'relative flex h-8 cursor-pointer select-none items-center gap-2 rounded-md pl-8 pr-2 text-sm outline-none pointer-coarse:h-10',
        'data-[highlighted]:bg-muted data-[state=checked]:font-bold',
        className,
      )}
      {...props}
    >
      <RadixMenu.ItemIndicator className="absolute left-2.5 flex text-accent">
        <Check size={14} aria-hidden />
      </RadixMenu.ItemIndicator>
      {children}
    </RadixMenu.RadioItem>
  );
}

/** A non-interactive heading inside a menu (e.g. the signed-in admin's name). */
export function DropdownMenuLabel({ className, ...props }: ComponentProps<typeof RadixMenu.Label>) {
  return <RadixMenu.Label className={cn('px-2 py-1.5 text-xs text-ink-muted', className)} {...props} />;
}

export function DropdownMenuSeparator({ className, ...props }: ComponentProps<typeof RadixMenu.Separator>) {
  return <RadixMenu.Separator className={cn('-mx-1 my-1 h-px bg-line', className)} {...props} />;
}
